import { Link } from 'react-router-dom';
import { useState } from 'react';

export default function AccountDeletion() {
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();

    // Create mailto link with pre-filled content
    const subject = encodeURIComponent('ZEPHYR AI - Account Deletion Request');
    const body = encodeURIComponent(
      `I would like to request the deletion of my ZEPHYR AI account.\n\n` +
      `Account Email: ${email}\n\n` +
      `Reason for deletion: ${reason}\n\n` +
      `I understand that:\n` +
      `- My account will be permanently deleted\n` +
      `- All my data will be removed from your servers\n` +
      `- This action cannot be undone\n\n` +
      `Please confirm the deletion of my account.`
    );

    window.location.href = `mailto:help@zeynsoft.com?subject=${subject}&body=${body}`;
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-dark-900">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-dark-900/80 backdrop-blur-lg border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-2">
            <img src="/logo.png" alt="ZEPHYR AI" className="w-10 h-10 rounded-2xl" />
            <span className="text-xl font-bold">ZEPHYR AI</span>
          </Link>
          <Link to="/" className="text-gray-400 hover:text-white transition">
            ← Back to Home
          </Link>
        </div>
      </nav>

      {/* Account Deletion Content */}
      <div className="pt-32 pb-20 px-6">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-5xl font-bold mb-6">Account Deletion Request</h1>
          <p className="text-gray-400 mb-12">Request permanent deletion of your ZEPHYR AI account and all associated data</p>

          <div className="prose prose-invert prose-lg max-w-none">
            {/* Information Section */}
            <section className="mb-12">
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-6 mb-8">
                <div className="flex items-start space-x-3">
                  <span className="text-2xl">⚠️</span>
                  <div>
                    <h3 className="text-xl font-semibold mb-2 text-amber-400">Important Information</h3>
                    <p className="text-gray-300 leading-relaxed mb-2">
                      Deleting your account is permanent and cannot be undone. Please read carefully before proceeding.
                    </p>
                  </div>
                </div>
              </div>

              <h2 className="text-3xl font-bold mb-4 text-white">What Will Be Deleted</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                When you delete your ZEPHYR AI account, the following data will be permanently removed:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2 mb-6">
                <li>Your account profile and authentication credentials</li>
                <li>Your preferences and app settings</li>
                <li>Subscription information (active subscriptions will be cancelled)</li>
                <li>Usage statistics and analytics data</li>
              </ul>

              <h2 className="text-3xl font-bold mb-4 text-white">Before You Delete</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                Please consider the following before requesting account deletion:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2 mb-6">
                <li><strong>Cancel Subscriptions:</strong> Your active subscription will be cancelled without refund</li>
                <li><strong>Processing Time:</strong> Account deletion requests are processed within 30 days</li>
                <li><strong>Cannot Be Undone:</strong> Once deleted, you cannot recover your account</li>
              </ul>

              <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-6 mb-8">
                <div className="flex items-start space-x-3">
                  <span className="text-2xl">ℹ️</span>
                  <div>
                    <h3 className="text-xl font-semibold mb-2 text-blue-400">Legal Compliance</h3>
                    <p className="text-gray-300 leading-relaxed mb-2">
                      Some information may be retained for legal and regulatory requirements:
                    </p>
                    <ul className="list-disc list-inside text-gray-300 space-y-1 text-sm">
                      <li>Transaction records (required for tax and accounting purposes)</li>
                      <li>Legal correspondence (required for dispute resolution)</li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            {/* Deletion Request Form */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold mb-6 text-white">Request Account Deletion</h2>

              {!submitted ? (
                <form onSubmit={handleSubmit} className="bg-dark-800 border border-white/10 rounded-xl p-8">
                  <div className="mb-6">
                    <label htmlFor="email" className="block text-white font-semibold mb-2">
                      Account Email Address <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your.email@example.com"
                      className="w-full px-4 py-3 bg-dark-900 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-primary transition"
                    />
                    <p className="text-gray-400 text-sm mt-2">
                      Enter the email address associated with your ZEPHYR AI account
                    </p>
                  </div>

                  <div className="mb-6">
                    <label htmlFor="reason" className="block text-white font-semibold mb-2">
                      Reason for Deletion (Optional)
                    </label>
                    <textarea
                      id="reason"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      rows="4"
                      placeholder="Please tell us why you're deleting your account (helps us improve)"
                      className="w-full px-4 py-3 bg-dark-900 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-primary transition resize-none"
                    />
                  </div>

                  <div className="mb-6">
                    <label className="flex items-start space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        required
                        className="mt-1 w-5 h-5 text-primary bg-dark-900 border-white/10 rounded focus:ring-primary focus:ring-2"
                      />
                      <span className="text-gray-300 text-sm">
                        I understand that deleting my account is permanent and cannot be undone. All my data will be permanently removed from ZEPHYR AI servers.
                      </span>
                    </label>
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-red-500 hover:bg-red-600 text-white font-semibold py-4 rounded-lg transition transform hover:scale-105"
                  >
                    Submit Deletion Request via Email
                  </button>

                  <p className="text-gray-400 text-sm text-center mt-4">
                    This will open your email client with a pre-filled deletion request
                  </p>
                </form>
              ) : (
                <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-8 text-center">
                  <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-4xl">✓</span>
                  </div>
                  <h3 className="text-2xl font-semibold mb-3 text-green-400">Email Client Opened</h3>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    Your email client should have opened with a pre-filled deletion request. Please send the email to complete your request.
                  </p>
                  <p className="text-gray-400 text-sm mb-6">
                    If your email client didn't open, you can manually send an email to <strong className="text-white">help@zeynsoft.com</strong> with the subject "Account Deletion Request" and include your account email address.
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="text-primary hover:text-primary/80 font-semibold transition"
                  >
                    ← Submit Another Request
                  </button>
                </div>
              )}
            </section>

            {/* Alternative Contact Methods */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold mb-4 text-white">Alternative Contact Method</h2>
              <p className="text-gray-300 leading-relaxed mb-6">
                If you prefer not to use the form above, you can contact us directly via email:
              </p>

              <div className="bg-dark-800 border border-white/10 rounded-xl p-8 text-center">
                <h3 className="text-2xl font-semibold mb-4 text-white flex items-center justify-center">
                  <span className="text-3xl mr-3">📧</span>
                  Email Support
                </h3>
                <p className="text-gray-300 mb-6">
                  Send your account deletion request directly to our support team:
                </p>
                <a
                  href="mailto:help@zeynsoft.com?subject=ZEPHYR%20AI%20-%20Account%20Deletion%20Request"
                  className="inline-block bg-primary hover:bg-primary/90 text-white font-bold text-lg px-8 py-4 rounded-lg transition transform hover:scale-105"
                >
                  help@zeynsoft.com
                </a>
                <p className="text-gray-400 text-sm mt-6">
                  Include your account email address in the message
                </p>
              </div>
            </section>

            {/* FAQ Section */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold mb-6 text-white">Frequently Asked Questions</h2>

              <div className="space-y-6">
                <div className="bg-dark-800 border border-white/10 rounded-xl p-6">
                  <h3 className="text-xl font-semibold mb-2 text-white">How long does deletion take?</h3>
                  <p className="text-gray-300">
                    Account deletion requests are processed within 30 days. You will receive a confirmation email once your account has been permanently deleted.
                  </p>
                </div>

                <div className="bg-dark-800 border border-white/10 rounded-xl p-6">
                  <h3 className="text-xl font-semibold mb-2 text-white">Can I recover my account after deletion?</h3>
                  <p className="text-gray-300">
                    No. Account deletion is permanent and cannot be undone. All your data will be permanently removed from our servers.
                  </p>
                </div>

                <div className="bg-dark-800 border border-white/10 rounded-xl p-6">
                  <h3 className="text-xl font-semibold mb-2 text-white">What happens to my subscription?</h3>
                  <p className="text-gray-300">
                    Any active subscriptions will be cancelled immediately upon account deletion. No refunds will be issued for unused subscription periods.
                  </p>
                </div>

                <div className="bg-dark-800 border border-white/10 rounded-xl p-6">
                  <h3 className="text-xl font-semibold mb-2 text-white">Will I receive confirmation?</h3>
                  <p className="text-gray-300">
                    Yes. You will receive an email confirmation when we receive your deletion request and another confirmation when your account has been permanently deleted.
                  </p>
                </div>

                <div className="bg-dark-800 border border-white/10 rounded-xl p-6">
                  <h3 className="text-xl font-semibold mb-2 text-white">Can I create a new account later?</h3>
                  <p className="text-gray-300">
                    Yes. After your account is deleted, you can create a new account using the same email address. However, none of your previous data will be available.
                  </p>
                </div>
              </div>
            </section>

            {/* Contact Information */}
            <section>
              <h2 className="text-3xl font-bold mb-4 text-white">Need Help?</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                If you have questions about account deletion or need assistance, please contact our support team:
              </p>
              <div className="bg-dark-800 border border-white/10 rounded-xl p-6">
                <ul className="text-gray-300 space-y-2">
                  <li><strong>Email:</strong> help@zeynsoft.com</li>
                  <li><strong>General Info:</strong> info@zeynsoft.com</li>
                  <li><strong>Website:</strong> <a href="https://zephyrai.zeynsoft.com" className="text-primary hover:text-primary/80 transition">https://zephyrai.zeynsoft.com</a></li>
                  <li><strong>Privacy Policy:</strong> <Link to="/privacy" className="text-primary hover:text-primary/80 transition">View Policy</Link></li>
                </ul>
              </div>
            </section>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 px-6">
        <div className="max-w-7xl mx-auto text-center text-gray-400">
          <p>© {new Date().getFullYear()} ZEPHYR AI by Zeynsoft. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
